import chunk from 'lodash/chunk.js';
import keyBy from 'lodash/keyBy';
import pThrottle from 'p-throttle';
import request from '@apps-common/js-client/api/request';
import {PDA_ADMIN_URL} from '@app/config';
import {removeEmpty} from './util.js';
import {useCallback} from 'react';
import {useQueries} from '@tanstack/react-query';

const throttle = pThrottle({
  limit: 4,
  interval: 1000,
});

const getEstimatesRequest = params =>
  request
    .post(`${PDA_ADMIN_URL}/quota-reservations/estimate`, params)
    .then(res => res.data);

const throttledGetEstimates = throttle(
  async params => await getEstimatesRequest(params)
);

export const queryKeys = {
  all: ['quotaEstimates'],
  lists: () => [...queryKeys.all, 'list'],
  list: filters => [...queryKeys.lists(), removeEmpty({...filters})],
  details: () => [...queryKeys.all, 'detail'],
  detail: id => [...queryKeys.details(), id],
};

export const useGetEstimates = (
  {aoi_refs, product_access_id} = {},
  queryOptions = {}
) => {
  const chunkedRefs = chunk(aoi_refs, 250);

  return useQueries({
    queries: chunkedRefs.map(refsChunk => ({
      queryKey: queryKeys.list({aoi_refs: refsChunk, product_access_id}),
      queryFn: () =>
        throttledGetEstimates({aoi_refs: refsChunk, product_access_id}),
      ...queryOptions,
      retry: 2,
      staleTime: 1 * 60 * 1000,
      select: data => {
        return keyBy(data.estimated_costs, 'aoi_ref');
      },
    })),
    combine: useCallback(results => {
      let data = {};
      for (const result of results) {
        data = {
          ...data,
          ...result.data,
        };
      }

      return {
        data,
        isLoading: results.some(result => result.isLoading),
        isPending: results.some(result => result.isPending),
        isFetching: results.some(result => result.isFetching),
        isRefetching: results.some(result => result.isRefetching),
        isSuccess: results.every(result => result.isSuccess),
        isError: results.some(result => result.isError),
        refetch: () => {
          return results.forEach(result => result.refetch());
        },
      };
    }, []),
  });
};
